import React, { useCallback, useEffect, useRef, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline'

import faqApi from '../../api/faq'
import SceltaLingua from './SceltaLingua'

const LANG_KEYS = ['italiano', 'inglese', 'tedesco', 'olandese', 'danese', 'polacco']

function editorPlainText(editorState) {
  if (!editorState || typeof editorState.getCurrentContent !== 'function') return ''
  try {
    return editorState.getCurrentContent().getPlainText('\n').trim()
  } catch {
    return ''
  }
}

function FAQScheda({
  lingua,
  cambiaLingua,
  faqAbilitato,
  cambiaFaqAbilitato,
  faqList,
  cambiaFaqItem,
  aggiungiFaqItem,
  rimuoviFaqItem,
  trimFaqListToLength,
  isModifica = true,
  titolo,
  contenuto,
  /** When set (e.g. CMS), overrides titolo/contenuto for AI context for the active language */
  cmsFaqContext,
}) {
  const [faqLoading, setFaqLoading] = useState(false)
  const [faqError, setFaqError] = useState(null)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [confirmOverwriteOpen, setConfirmOverwriteOpen] = useState(false)
  const faqRefs = useRef([])
  const scrollToIndexRef = useRef(null)

  const currentLangKey = LANG_KEYS[lingua - 1] || LANG_KEYS[0]
  const currentLangFields = faqList
    .map((item) => ({
      domanda: item?.[`domanda_${currentLangKey}`] || '',
      risposta: item?.[`risposta_${currentLangKey}`] || '',
    }))
    .filter((item) => item.domanda.trim().length > 0 || item.risposta.trim().length > 0)

  const applyFaqList = useCallback(
    (generatedFaqs) => {
      if (!Array.isArray(generatedFaqs) || generatedFaqs.length === 0) return

      const currentLength = faqList.length
      const targetLength = generatedFaqs.length

      if (targetLength > currentLength) {
        for (let i = 0; i < targetLength - currentLength; i += 1) {
          aggiungiFaqItem()
        }
      }
      if (targetLength < currentLength && typeof trimFaqListToLength === 'function') {
        trimFaqListToLength(targetLength)
      } else if (targetLength < currentLength) {
        for (let i = currentLength - 1; i >= targetLength; i -= 1) {
          rimuoviFaqItem(i)
        }
      }

      generatedFaqs.forEach((faq, index) => {
        cambiaFaqItem(index, `domanda_${currentLangKey}`, faq.question || '')
        cambiaFaqItem(index, `risposta_${currentLangKey}`, faq.answer || '')
      })
    },
    [faqList.length, currentLangKey, cambiaFaqItem, aggiungiFaqItem, rimuoviFaqItem, trimFaqListToLength]
  )

  const doGenerateFaq = useCallback(async () => {
    if (faqLoading) return
    setFaqLoading(true)
    setFaqError(null)
    setConfirmOverwriteOpen(false)
    try {
      const pageTitle =
        cmsFaqContext != null
          ? String(cmsFaqContext.pageTitle ?? '').trim()
          : titolo?.[currentLangKey] != null
            ? String(titolo[currentLangKey]).trim()
            : ''
      const pageDescription =
        cmsFaqContext != null
          ? String(cmsFaqContext.pageBody ?? '').trim()
          : editorPlainText(contenuto?.[currentLangKey])
      const faqContextForLang = faqList.map((item) => ({
        domanda: item[`domanda_${currentLangKey}`] ?? '',
        risposta: item[`risposta_${currentLangKey}`] ?? '',
      }))
      const response = await faqApi.generateFaq(currentLangKey, faqContextForLang, {
        pageTitle,
        pageDescription,
      })
      if (!response.ok || !response.data?.faqs) {
        setFaqError(response.data?.error || 'Generation failed.')
        setSnackbarOpen(true)
        return
      }
      applyFaqList(response.data.faqs)
    } catch (err) {
      setFaqError(err?.message || 'Generation failed.')
      setSnackbarOpen(true)
    } finally {
      setFaqLoading(false)
    }
  }, [faqLoading, currentLangKey, faqList, applyFaqList, titolo, contenuto, cmsFaqContext])

  const handleGenerateFaq = useCallback(() => {
    if (faqLoading) return
    if (currentLangFields.length > 0) {
      setConfirmOverwriteOpen(true)
      return
    }
    doGenerateFaq()
  }, [faqLoading, currentLangFields.length, doGenerateFaq])

  const handleAddFaqItem = useCallback(() => {
    if (faqLoading) return
    scrollToIndexRef.current = faqList.length
    aggiungiFaqItem()
  }, [faqLoading, faqList.length, aggiungiFaqItem])

  useEffect(() => {
    const targetIndex = scrollToIndexRef.current
    if (targetIndex == null) return
    const node = faqRefs.current[targetIndex]
    if (!node) return
    node.scrollIntoView({ behavior: 'smooth', block: 'start' })
    scrollToIndexRef.current = null
  }, [faqList.length])

  return (
    <>
      <div className={isModifica ? 'admin-container-lungo' : 'admin-container'}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
          <FormControlLabel
            control={<Switch checked={Boolean(faqAbilitato)} onChange={cambiaFaqAbilitato} color="primary" />}
            label="Abilita sezione FAQ"
          />
          {faqAbilitato && (
            <Button
              variant="contained"
              size="medium"
              disabled={faqLoading}
              onClick={handleGenerateFaq}
              startIcon={faqLoading ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                mt: '1rem',
                gap: '8px',
              }}
            >
              {faqLoading ? 'Generating...' : 'Genera con AI'}
              <AutoAwesomeIcon />
            </Button>
          )}
        </Stack>
        <Box
          sx={{
            opacity: faqAbilitato ? 1 : 0.6,
            pointerEvents: faqAbilitato ? 'auto' : 'none',
            marginTop: 2,
          }}
        >
          <SceltaLingua lingua={lingua} cambiaLingua={cambiaLingua} titolo="Lingua" />
          <Button
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleAddFaqItem}
            variant="outlined"
            size="small"
            sx={{ marginTop: 2 }}
            disabled={faqLoading}
          >
            Aggiungi domanda
          </Button>
          <Stack spacing={2} sx={{ marginTop: 2 }}>
            {faqList.map((item, index) => (
              <Box
                key={item._faqKey ?? `faq-row-${index}`}
                ref={(node) => {
                  faqRefs.current[index] = node
                }}
                sx={{
                  p: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  backgroundColor: '#fafafa',
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle2" color="text.secondary">
                    Domanda {index + 1}
                  </Typography>
                  <IconButton
                    type="button"
                    size="small"
                    onClick={() => rimuoviFaqItem(index)}
                    disabled={faqLoading}
                    aria-label="Rimuovi domanda"
                  >
                    <RemoveCircleOutlineIcon />
                  </IconButton>
                </Stack>
                <TextField
                  fullWidth
                  label="Domanda"
                  value={item[`domanda_${currentLangKey}`] ?? ''}
                  onChange={(e) => cambiaFaqItem(index, `domanda_${currentLangKey}`, e.target.value)}
                  margin="normal"
                  size="small"
                  multiline
                  maxRows={3}
                  disabled={faqLoading}
                />
                <TextField
                  fullWidth
                  label="Risposta"
                  value={item[`risposta_${currentLangKey}`] ?? ''}
                  onChange={(e) => cambiaFaqItem(index, `risposta_${currentLangKey}`, e.target.value)}
                  margin="normal"
                  size="small"
                  multiline
                  minRows={2}
                  maxRows={6}
                  disabled={faqLoading}
                />
              </Box>
            ))}
          </Stack>
        </Box>
      </div>
      <Dialog open={confirmOverwriteOpen} onClose={() => setConfirmOverwriteOpen(false)}>
        <DialogTitle>Aggiornamento FAQ</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Le FAQ attuali per la lingua selezionata verranno aggiornate con i nuovi valori generati. Sei sicuro di voler
            continuare?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOverwriteOpen(false)}>Cancel</Button>
          <Button onClick={doGenerateFaq} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
          {faqError || 'Generation failed.'}
        </Alert>
      </Snackbar>
    </>
  )
}

export default FAQScheda
